import { nectarApi } from './client'

const PENDING_KEY = 'dh:pending-campaign-claim'
const MISSION_PATH_RE = /\/missions\/nectar\/([^/]+)/

interface PendingClaim {
  projectId: string
  code: string
  utmSource?: string
  utmMedium?: string
}

const readPending = (): PendingClaim | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY)
    return raw ? (JSON.parse(raw) as PendingClaim) : null
  } catch {
    return null
  }
}

const clearPending = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(PENDING_KEY)
  } catch {
    // sessionStorage unavailable — claim is best-effort, ignore.
  }
}

/**
 * Captures an ad-campaign claim from the current URL, bound to the project the link points at.
 * Call once at startup (before the mission page mounts). We persist {projectId, code, utm*} so the
 * claim can ONLY ever apply to that project: a utm_campaign lingering in the session can never leak
 * a grant into a different project the user happens to open later. Survives the auth round-trip via
 * sessionStorage (same as utmParams).
 */
export const captureCampaignClaim = (): void => {
  if (typeof window === 'undefined') return
  const match = MISSION_PATH_RE.exec(window.location.pathname)
  if (!match) return
  const params = new URLSearchParams(window.location.search)
  const code = params.get('utm_campaign')
  if (!code) return
  const pending: PendingClaim = {
    projectId: match[1],
    code,
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined
  }
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending))
  } catch {
    // sessionStorage unavailable — claim is best-effort, ignore.
  }
}

/**
 * Self-serve whitelist claim for ad traffic. If a campaign claim was captured for THIS project,
 * posts it to the Nectar API BEFORE the project is loaded, so an invite-only project's whitelist
 * gate is already passed on the first project read. Best-effort: any failure is swallowed and the
 * caller proceeds to load the project unchanged. One-shot — the pending claim is consumed (cleared)
 * once the API has answered, so it never repeats and never applies to another project.
 */
export const claimCampaignOnce = async (projectId: string): Promise<void> => {
  const pending = readPending()
  if (!pending || pending.projectId !== projectId) return
  try {
    await nectarApi.post(`/v1/projects/${projectId}/campaign-claim`, {
      code: pending.code,
      utmSource: pending.utmSource,
      utmMedium: pending.utmMedium
    })
    clearPending()
  } catch (err) {
    console.warn('Campaign claim failed; proceeding to load project', err)
  }
}
